import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { SERVICE_CATEGORIES, fmtINR } from "../lib/constants";
import { Modal, Input, Select } from "./Leads";
import {
  Plus, Search, Filter, Package, Sparkles, MapPin, Eye, ArrowUpDown,
  ChevronLeft, ChevronRight, Briefcase, X, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 12;

// A stage → colour mapping (keeps chips readable across service types)
const stageChipClass = (stage) => {
  const s = (stage || "").toLowerCase();
  if (!s) return "bg-stone-100 text-stone-600";
  if (s.includes("granted") || s.includes("approved") || s.includes("issued")) return "bg-emerald-100 text-emerald-800";
  if (s.includes("submitted") || s.includes("review") || s.includes("scrutiny")) return "bg-sky-100 text-sky-800";
  if (s.includes("payment")) return "bg-amber-100 text-amber-800";
  return "bg-stone-100 text-stone-700";
};

export default function Projects() {
  const [searchParams, setSearchParams] = useSearchParams();
  const assignedParam = searchParams.get("assigned") || "";
  const delayedParam = searchParams.get("delayed") === "1";

  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [delayedIds, setDelayedIds] = useState(null); // Set or null
  const [suggestedProduct, setSuggestedProduct] = useState(null);

  const [open, setOpen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [stageFilter, setStageFilter] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [page, setPage] = useState(1);

  const [form, setForm] = useState({
    client_name: "", client_email: "", client_phone: "",
    project_name: "", project_location: "",
    contract_value: 0, service_category: "Environmental Clearance",
    service_type: "Fresh EC", product_key: "", project_lead: "", compliance_executive: "",
    monitoring_executive: "", hod: "",
    start_date: new Date().toISOString().slice(0, 10),
    target_completion_date: "",
  });

  const load = async () => {
    const [p, u, pr] = await Promise.all([api.get("/projects"), api.get("/users"), api.get("/products")]);
    setItems(p.data);
    setUsers(u.data);
    setProducts(pr.data);
  };

  useEffect(() => { load(); }, []);

  // Fetch delayed ids only when delayed filter is on (super_admin only endpoint)
  useEffect(() => {
    if (!delayedParam) { setDelayedIds(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/dashboard-kpis");
        if (!cancelled) setDelayedIds(new Set(data.delayed_projects.map((p) => p.id)));
      } catch { if (!cancelled) setDelayedIds(new Set()); }
    })();
    return () => { cancelled = true; };
  }, [delayedParam]);

  // Suggest a product whenever service_type changes
  useEffect(() => {
    if (form.service_category !== "Environmental Systems") { setSuggestedProduct(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/products/by-service/${encodeURIComponent(form.service_type)}`);
        if (!cancelled) setSuggestedProduct(data.product);
      } catch { if (!cancelled) setSuggestedProduct(null); }
    })();
    return () => { cancelled = true; };
  }, [form.service_type, form.service_category]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/projects", { ...form, contract_value: Number(form.contract_value) });
      const cred = data._new_client_credentials;
      if (cred) {
        const copyText = `Client Portal Login\nURL: ${window.location.origin}/login\nEmail: ${cred.email}\nInitial Password: ${cred.initial_password}\n(You will be asked to reset the password on first login.)`;
        toast.success(
          (t) => (
            <div className="flex flex-col gap-1.5">
              <div className="font-semibold text-emerald-900">Project created + client login provisioned</div>
              <div className="text-xs text-stone-700"><b>Email:</b> {cred.email}</div>
              <div className="text-xs text-stone-700"><b>Password:</b> {cred.initial_password} <span className="text-stone-400">(force-reset on first login)</span></div>
              <button
                type="button"
                data-testid="copy-client-credentials"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(copyText);
                    toast.success("Credentials copied to clipboard");
                  } catch {
                    toast.error("Copy failed — please copy manually from the toast");
                  }
                }}
                className="mt-1 self-start bg-emerald-700 hover:bg-emerald-800 text-white text-xs px-2.5 py-1 rounded font-medium"
              >
                Copy login details
              </button>
            </div>
          ),
          { duration: 25000 }
        );
      } else {
        toast.success("Project created — checklist + 14 folders + requirements auto-loaded");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const userName = (id) => users.find((u) => u.id === id)?.name || "—";

  const assignedUser = users.find((u) => u.id === assignedParam);
  const clearAssigned = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("assigned");
    setSearchParams(next, { replace: true });
  };
  const clearDelayed = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("delayed");
    setSearchParams(next, { replace: true });
  };

  const categories = ["All", ...Object.keys(SERVICE_CATEGORIES)];
  const allStages = useMemo(() => {
    const s = new Set();
    items.forEach((p) => { if (p.current_stage) s.add(p.current_stage); });
    return Array.from(s).sort();
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter((p) => {
        if (categoryFilter !== "All" && p.service_category !== categoryFilter) return false;
        if (stageFilter && p.current_stage !== stageFilter) return false;
        if (assignedParam) {
          const assignedKeys = ["project_lead", "hod", "compliance_executive", "monitoring_executive", "associate", "reviewer"];
          if (!assignedKeys.some((k) => p[k] === assignedParam)) return false;
        }
        if (delayedParam && delayedIds && !delayedIds.has(p.id)) return false;
        if (!q) return true;
        const hay = [
          p.project_id_external, p.project_name, p.client_name, p.project_location,
          p.service_category, p.service_type, p.current_stage, p.product_name,
        ].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) => {
        const k = sort.key;
        const av = (a[k] || "").toString().toLowerCase();
        const bv = (b[k] || "").toString().toLowerCase();
        if (k === "contract_value") {
          const na = Number(a.contract_value || 0), nb = Number(b.contract_value || 0);
          return sort.dir === "asc" ? na - nb : nb - na;
        }
        if (av < bv) return sort.dir === "asc" ? -1 : 1;
        if (av > bv) return sort.dir === "asc" ? 1 : -1;
        return 0;
      });
  }, [items, categoryFilter, stageFilter, search, sort, assignedParam, delayedParam, delayedIds]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const toggleSort = (k) => setSort((s) => ({ key: k, dir: s.key === k && s.dir === "asc" ? "desc" : "asc" }));

  const teamOpts = users.filter((u) => u.role !== "client").map((u) => ({ value: u.id, label: `${u.name}` }));

  const headers = [
    { k: "project_id_external", label: "Project ID" },
    { k: "project_name", label: "Project" },
    { k: "client_name", label: "Client", hideOnMobile: true },
    { k: "project_location", label: "Location", hideOnMobile: true },
    { k: "service_type", label: "Service", hideOnMobile: true },
    { k: "project_lead", label: "Lead", hideOnMobile: true },
    { k: "current_stage", label: "Stage" },
    { k: "contract_value", label: "Value" },
  ];

  return (
    <div className="space-y-5 animate-fade-up" style={{ fontFamily: "Poppins, sans-serif" }} data-testid="projects-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#72BE44] font-semibold mb-2">Operations</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0F2A4A]">Projects</h1>
          <p className="text-stone-500 mt-1 text-sm">{items.length} projects · {filtered.length} matching filters</p>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="new-project-btn"
          className="flex items-center gap-2 text-white px-4 py-2 rounded-lg text-sm font-medium"
          style={{ background: "#0F2A4A" }}
        >
          <Plus className="w-4 h-4" /> New Project
        </button>
      </div>

      {/* Active filter chips (from URL params) */}
      {(assignedUser || delayedParam) && (
        <div className="flex flex-wrap items-center gap-2" data-testid="active-filters">
          {assignedUser && (
            <span className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-300 text-emerald-900 text-xs px-3 py-1.5 rounded-full">
              <Filter className="w-3.5 h-3.5" />
              Assigned to: <b>{assignedUser.name}</b>
              <button onClick={clearAssigned} data-testid="clear-assigned" className="ml-1 hover:text-rose-600">
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          )}
          {delayedParam && (
            <span className="inline-flex items-center gap-2 bg-rose-50 border border-rose-300 text-rose-900 text-xs px-3 py-1.5 rounded-full">
              <AlertTriangle className="w-3.5 h-3.5" />
              Delayed only
              <button onClick={clearDelayed} data-testid="clear-delayed" className="ml-1 hover:text-rose-700">
                <X className="w-3.5 h-3.5" />
              </button>
            </span>
          )}
        </div>
      )}

      {/* Category pills */}
      <div className="flex flex-wrap gap-2">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setCategoryFilter(c)}
            data-testid={`filter-${c}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${
              categoryFilter === c
                ? "bg-[#0F2A4A] text-white border-[#0F2A4A]"
                : "bg-white border-stone-200 text-stone-600 hover:border-stone-400"
            }`}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Search + stage filter bar */}
      <div className="bg-white border border-stone-200 rounded-xl p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="project-search"
            placeholder="Search by project, client, location, product..."
            className="w-full pl-10 pr-3 py-2 bg-stone-50 border border-transparent rounded-lg focus:bg-white focus:border-[#72BE44] focus:outline-none text-sm transition"
          />
        </div>
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          data-testid="filter-stage"
          className="text-xs px-2.5 py-2 bg-stone-50 border border-stone-200 rounded-lg focus:outline-none focus:border-[#72BE44]"
        >
          <option value="">All Stages</option>
          {allStages.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        {(search || stageFilter || categoryFilter !== "All") && (
          <button
            onClick={() => { setSearch(""); setStageFilter(""); setCategoryFilter("All"); }}
            className="text-xs text-stone-500 hover:text-rose-600 px-2 py-1"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#0F2A4A] text-white text-[11px] uppercase tracking-wider sticky top-0">
              <tr>
                {headers.map((h) => (
                  <th
                    key={h.k}
                    onClick={() => toggleSort(h.k)}
                    data-testid={`sort-${h.k}`}
                    className={`px-3 py-3 text-left font-semibold cursor-pointer hover:bg-white/10 ${h.hideOnMobile ? "hidden md:table-cell" : ""}`}
                  >
                    <span className="inline-flex items-center gap-1">
                      {h.label}
                      {sort.key === h.k && <ArrowUpDown className="w-3 h-3" />}
                    </span>
                  </th>
                ))}
                <th className="px-3 py-3 text-right font-semibold">Progress</th>
                <th className="px-3 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((p) => {
                const stages = p.stages || [];
                const idx = stages.indexOf(p.current_stage);
                const progress = stages.length ? Math.max(((idx + 1) / stages.length) * 100, 5) : 0;
                return (
                  <tr
                    key={p.id}
                    data-testid={`project-row-${p.id}`}
                    className="border-t border-stone-100 hover:bg-[#72BE44]/5 transition"
                  >
                    <td className="px-3 py-2.5 font-mono text-xs text-stone-700">
                      <Link to={`/projects/${p.id}`} className="hover:underline">
                        {p.project_id_external || (p.id || "").slice(0, 8)}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5">
                      <Link to={`/projects/${p.id}`} className="font-medium text-[#0F2A4A] hover:underline">
                        {p.project_name}
                      </Link>
                      {p.product_name && (
                        <div className="text-[10px] text-emerald-700 flex items-center gap-1 mt-0.5">
                          <Package className="w-3 h-3" /> {p.product_name}
                        </div>
                      )}
                    </td>
                    <td className="hidden md:table-cell px-3 py-2.5 text-stone-700">{p.client_name}</td>
                    <td className="hidden md:table-cell px-3 py-2.5 text-xs text-stone-600">
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-stone-400" />{p.project_location || "—"}
                      </span>
                    </td>
                    <td className="hidden md:table-cell px-3 py-2.5">
                      <span className="text-[10px] uppercase tracking-wider bg-stone-100 text-stone-700 px-2 py-0.5 rounded-full">
                        {p.service_type}
                      </span>
                    </td>
                    <td className="hidden md:table-cell px-3 py-2.5 text-xs">{userName(p.project_lead)}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-semibold ${stageChipClass(p.current_stage)}`}>
                        {p.current_stage || "Not started"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-sm font-semibold text-stone-900">{fmtINR(p.contract_value)}</td>
                    <td className="px-3 py-2.5">
                      <div className="w-24 ml-auto">
                        <div className="text-[10px] text-stone-500 text-right mb-0.5">{idx + 1}/{stages.length || 0}</div>
                        <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-600 rounded-full" style={{ width: `${progress}%` }} />
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <Link
                        to={`/projects/${p.id}`}
                        data-testid={`act-view-${p.id}`}
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-1.5 rounded hover:bg-[#72BE44]/15 text-[#0F2A4A] transition"
                      >
                        <Eye className="w-3.5 h-3.5" /> Open
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {pageItems.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center py-16 text-stone-400">
                    <Briefcase className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    No projects match your filters
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-stone-100 text-xs text-stone-600">
          <div>
            Showing <b>{Math.min((page - 1) * PAGE_SIZE + 1, filtered.length)}</b> – <b>{Math.min(page * PAGE_SIZE, filtered.length)}</b> of <b>{filtered.length}</b>
          </div>
          <div className="flex items-center gap-1">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              data-testid="page-prev"
              className="p-1.5 rounded hover:bg-stone-100 disabled:opacity-30"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-3 py-1 bg-stone-100 rounded font-medium">{page} / {totalPages}</span>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              data-testid="page-next"
              className="p-1.5 rounded hover:bg-stone-100 disabled:opacity-30"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {open && (
        <Modal title="New Project" onClose={() => setOpen(false)}>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input label="Client Name" value={form.client_name} onChange={(v) => setForm({ ...form, client_name: v })} required testid="proj-client" />
              <Input label="Project Name" value={form.project_name} onChange={(v) => setForm({ ...form, project_name: v })} required testid="proj-name" />
            </div>
            <div className="bg-[#72BE44]/10 border border-[#72BE44]/30 rounded-lg p-3">
              <div className="text-[11px] uppercase tracking-wider text-[#0F2A4A] font-bold mb-2 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-[#72BE44]" /> Client Portal Access (auto-create on save)
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input label="Client Email (becomes login)" type="email" value={form.client_email} onChange={(v) => setForm({ ...form, client_email: v })} testid="proj-client-email" />
                <Input label="Client Phone (becomes initial password)" value={form.client_phone} onChange={(v) => setForm({ ...form, client_phone: v })} testid="proj-client-phone" />
              </div>
              <p className="text-[10px] text-stone-600 mt-2 italic">
                If a client user already exists with this email, the project will be linked to them. Otherwise a new client login is auto-created with the phone number as initial password (forced reset on first login).
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Location" value={form.project_location} onChange={(v) => setForm({ ...form, project_location: v })} required testid="proj-location" />
              <Input label="Contract Value (₹)" type="number" value={form.contract_value} onChange={(v) => setForm({ ...form, contract_value: v })} required testid="proj-value" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select
                label="Service Category"
                value={form.service_category}
                onChange={(v) => setForm({ ...form, service_category: v, service_type: SERVICE_CATEGORIES[v][0] })}
                options={Object.keys(SERVICE_CATEGORIES)}
                testid="proj-category"
              />
              <Select
                label="Service Type"
                value={form.service_type}
                onChange={(v) => setForm({ ...form, service_type: v, product_key: "" })}
                options={SERVICE_CATEGORIES[form.service_category]}
                testid="proj-type"
              />
            </div>

            {form.service_category === "Environmental Systems" && (
              <div className="bg-emerald-50/40 border border-emerald-200 rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-emerald-800 font-semibold">
                  <Package className="w-3.5 h-3.5" /> PTESPL Product Link
                </div>
                <Select
                  label="Link a Product (auto-fills scope)"
                  value={form.product_key || (suggestedProduct?.key || "")}
                  onChange={(v) => setForm({ ...form, product_key: v })}
                  options={[
                    { value: "", label: "— No product linked —" },
                    ...products.map((p) => ({ value: p.key, label: `${p.name} — ${p.category}` })),
                  ]}
                  testid="proj-product"
                />
                {suggestedProduct && !form.product_key && (
                  <div className="flex items-start gap-2 text-xs text-emerald-900 bg-white border border-emerald-200 rounded-lg p-2.5">
                    <Sparkles className="w-3.5 h-3.5 mt-0.5 text-emerald-700 flex-shrink-0" />
                    <div>
                      <b>Suggested:</b> {suggestedProduct.name} ({suggestedProduct.tagline}). Will be auto-linked on save unless you choose otherwise.
                    </div>
                  </div>
                )}
                {(form.product_key || suggestedProduct?.key) && (() => {
                  const sel = products.find((p) => p.key === (form.product_key || suggestedProduct?.key));
                  if (!sel) return null;
                  return (
                    <div className="bg-white border border-stone-200 rounded-lg p-3" data-testid="product-preview">
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-semibold text-sm text-stone-900">{sel.name}</div>
                        <span className="text-[10px] uppercase tracking-wider bg-stone-100 text-stone-700 px-2 py-0.5 rounded-full">{sel.category}</span>
                      </div>
                      <div className="text-xs text-emerald-700 font-medium">{sel.tagline}</div>
                      <p className="text-xs text-stone-600 mt-1.5 leading-relaxed">{sel.description}</p>
                      <div className="text-[10px] text-stone-500 mt-2">Scope will be auto-populated from product details on save.</div>
                    </div>
                  );
                })()}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Select label="Project Lead" value={form.project_lead} onChange={(v) => setForm({ ...form, project_lead: v })} options={[{ value: "", label: "—" }, ...teamOpts]} testid="proj-lead" />
              <Select label="HOD" value={form.hod} onChange={(v) => setForm({ ...form, hod: v })} options={[{ value: "", label: "—" }, ...teamOpts]} testid="proj-hod" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Start Date" type="date" value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} testid="proj-start" />
              <Input label="Target Completion Date (optional override)" type="date" value={form.target_completion_date} onChange={(v) => setForm({ ...form, target_completion_date: v })} testid="proj-target" />
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800">
              ✨ A document checklist, 14 numbered folders, service-specific requirement list, and project charter will be auto-loaded.
            </div>
            <button type="submit" data-testid="proj-submit" className="w-full bg-emerald-700 text-white py-2.5 rounded-lg font-medium">Create Project</button>
          </form>
        </Modal>
      )}
    </div>
  );
}
